import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import api, { fmtMoney, fmtDate, formatApiError, fmtNum } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
    CheckCircle2, Send, Truck, Receipt, PackageCheck, Loader2, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { can } from "../lib/rbac";

export default function PurchaseOrderDetail() {
    const { user } = useAuth();
    const canApprove = can(user, "po.approve");
    const canSend = can(user, "po.send");
    const canGr = can(user, "gr.create");
    const canInv = can(user, "invoice.create");
    const { id } = useParams();
    const nav = useNavigate();
    const [po, setPo] = useState(null);
    const [grs, setGrs] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [grOpen, setGrOpen] = useState(false);
    const [invOpen, setInvOpen] = useState(false);

    const load = () => Promise.all([
        api.get(`/purchase-orders/${id}`),
        api.get(`/goods-receipts?po_id=${id}`),
        api.get(`/invoices?po_id=${id}`),
    ]).then(([p, g, i]) => { setPo(p.data); setGrs(g.data); setInvoices(i.data); });

    useEffect(() => { load(); }, [id]);

    if (!po) return <AppShell title="Sipariş"><div className="py-20 text-center"><Loader2 className="animate-spin inline"/></div></AppShell>;

    const action = async (a) => {
        try {
            await api.post(`/purchase-orders/${id}/${a}`);
            toast.success("İşlem başarılı"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell
            title={`Sipariş ${po.number}`}
            subtitle="Satın Alma"
            actions={
                <div className="flex gap-2">
                    {po.status === "draft" && canApprove && (po.created_by !== user?.id || user?.role === "admin") && (
                        <Button data-testid="po-approve" onClick={()=>action("approve")}><CheckCircle2 size={14} className="mr-2"/> Onayla</Button>
                    )}
                    {po.status === "approved" && canSend && (
                        <Button data-testid="po-send" onClick={()=>action("send")}><Send size={14} className="mr-2"/> Tedarikçiye Gönder</Button>
                    )}
                    {["approved", "sent", "partially_received"].includes(po.status) && canGr && (
                        <Button data-testid="po-new-gr" variant="outline" onClick={()=>setGrOpen(true)}><PackageCheck size={14} className="mr-2"/> Mal Kabul</Button>
                    )}
                    {grs.length > 0 && canInv && (
                        <Button data-testid="po-new-inv" variant="outline" onClick={()=>setInvOpen(true)}><Receipt size={14} className="mr-2"/> Fatura</Button>
                    )}
                </div>
            }
        >
            {po.status === "draft" && canApprove && po.created_by === user?.id && user?.role !== "admin" && (
                <div
                    data-testid="po-self-approve-banner"
                    className="mb-4 text-sm text-amber-800 border border-amber-300 bg-amber-50 rounded-md px-3 py-2"
                >
                    Kendi oluşturduğunuz siparişi onaylayamazsınız. Onay başka bir Yönetici/Müdür tarafından verilmelidir.
                </div>
            )}
            <Button variant="ghost" size="sm" onClick={()=>nav("/purchase-orders")} className="mb-4"><ArrowLeft size={14} className="mr-2"/> Siparişler</Button>

            <div className="grid lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 bg-card border border-border rounded-md p-5">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{po.number}</div>
                            <div className="text-xl font-semibold mt-1">{po.supplier_name}</div>
                            <div className="text-sm text-muted-foreground">{po.project_code} — {po.project_name}</div>
                        </div>
                        <StatusBadge status={po.status}/>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-sm border-t border-border pt-4">
                        <Info label="Tarih" value={fmtDate(po.date)}/>
                        <Info label="Teslim" value={fmtDate(po.delivery_date)}/>
                        <Info label="Vade" value={`${po.payment_term_days} gün`}/>
                        <Info label="Para Birimi" value={po.currency}/>
                    </div>
                </div>
                <div className="bg-card border border-border rounded-md p-5">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Toplam</div>
                    <div className="text-3xl font-semibold mt-1 tabular-nums">{fmtMoney(po.total_amount, po.currency)}</div>
                    <div className="mt-4 space-y-1.5 text-xs">
                        <div className="flex justify-between"><span className="text-muted-foreground">Ara Toplam</span><span className="tabular-nums">{fmtMoney(po.subtotal, po.currency)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">İndirim</span><span className="tabular-nums">-{fmtMoney(po.total_discount, po.currency)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">KDV</span><span className="tabular-nums">+{fmtMoney(po.total_vat, po.currency)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Nakliye</span><span className="tabular-nums">+{fmtMoney(po.freight_cost, po.currency)}</span></div>
                    </div>
                </div>
            </div>

            {/* Lines */}
            <div className="bg-card border border-border rounded-md mb-6">
                <div className="p-4 border-b border-border">
                    <div className="text-sm font-medium">Kalemler</div>
                </div>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>SKU</th><th>Ürün</th>
                            <th className="text-right">Sipariş</th>
                            <th className="text-right">Teslim Alınan</th>
                            <th className="text-right">Kalan</th>
                            <th className="text-right">Birim Fiyat</th>
                            <th className="text-right">Toplam</th>
                        </tr>
                    </thead>
                    <tbody>
                        {po.lines?.map(l => (
                            <tr key={l.line_id}>
                                <td className="font-mono text-xs">{l.sku}</td>
                                <td className="font-medium">{l.name}</td>
                                <td className="text-right tabular-nums">{fmtNum(l.quantity, 0)} {l.unit}</td>
                                <td className="text-right tabular-nums text-emerald-600">{fmtNum(l.received_qty, 0)}</td>
                                <td className="text-right tabular-nums text-amber-600">{fmtNum(l.quantity - l.received_qty, 0)}</td>
                                <td className="text-right tabular-nums">{fmtMoney(l.unit_price, po.currency)}</td>
                                <td className="text-right tabular-nums font-medium">{fmtMoney(l.line_total, po.currency)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Related GRs & Invoices */}
            <div className="grid lg:grid-cols-2 gap-4">
                <RelatedList title="Mal Kabuller" items={grs} onClick={(g)=>nav(`/goods-receipts`)} renderItem={(g)=>(
                    <>
                        <div className="font-mono text-xs">{g.number}</div>
                        <div className="text-xs text-muted-foreground">{fmtDate(g.date)}</div>
                    </>
                )}/>
                <RelatedList title="Faturalar" items={invoices} onClick={(inv)=>nav(`/invoices/${inv.id}`)} renderItem={(inv)=>(
                    <>
                        <div className="font-mono text-xs">{inv.number} <span className="text-muted-foreground">/ {inv.supplier_invoice_number}</span></div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">{fmtDate(inv.invoice_date)} <StatusBadge status={inv.match_status}/></div>
                    </>
                )}/>
            </div>

            <div className="mt-4">
                <AttachmentsPanel entityType="po" entityId={po.id}/>
            </div>

            {/* Goods Receipt dialog */}
            <GRDialog open={grOpen} setOpen={setGrOpen} po={po} onDone={load}/>
            <InvoiceDialog open={invOpen} setOpen={setInvOpen} po={po} grs={grs} onDone={load}/>
        </AppShell>
    );
}

function Info({label,value}){return<div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div><div className="mt-0.5 text-sm font-medium">{value}</div></div>}

function RelatedList({ title, items, renderItem, onClick }) {
    return (
        <div className="bg-card border border-border rounded-md">
            <div className="p-3 border-b border-border text-sm font-medium">{title} ({items.length})</div>
            <div className="divide-y divide-border">
                {items.map(x => (
                    <button key={x.id} onClick={()=>onClick(x)} className="block w-full text-left px-4 py-3 hover:bg-muted transition-colors">
                        {renderItem(x)}
                    </button>
                ))}
                {items.length === 0 && <div className="px-4 py-6 text-center text-muted-foreground text-sm">Kayıt yok</div>}
            </div>
        </div>
    );
}

function GRDialog({ open, setOpen, po, onDone }) {
    const [lines, setLines] = useState([]);
    useEffect(() => {
        if (open) setLines(po.lines.map(pl => ({
            po_line_id: pl.line_id, received_qty: pl.quantity - pl.received_qty,
            accepted_qty: pl.quantity - pl.received_qty, rejected_qty: 0, warehouse: "MAIN",
        })));
    }, [open, po]);

    const upd = (i, f, v) => { const nl = [...lines]; nl[i][f] = v; setLines(nl); };
    const submit = async () => {
        try {
            const payload = { po_id: po.id, warehouse: "MAIN", lines: lines.map(l => ({
                po_line_id: l.po_line_id,
                product_id: "", sku: "", name: "", unit: "", ordered_qty: 0,
                received_qty: Number(l.received_qty), accepted_qty: Number(l.accepted_qty),
                rejected_qty: Number(l.rejected_qty || 0), warehouse: l.warehouse,
            })) };
            await api.post("/goods-receipts", payload);
            toast.success("Mal kabul tamamlandı. Stok güncellendi.");
            setOpen(false); onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-3xl">
                <DialogHeader><DialogTitle>Mal Kabul — {po.number}</DialogTitle></DialogHeader>
                <div className="border border-border rounded-md overflow-hidden">
                    <table className="data-table">
                        <thead>
                            <tr><th>Ürün</th><th className="text-right">Sipariş</th><th className="text-right">Kalan</th><th className="text-right">Kabul</th><th className="text-right">Red</th></tr>
                        </thead>
                        <tbody>
                            {po.lines.map((pl, i) => {
                                const remaining = pl.quantity - pl.received_qty;
                                return (
                                    <tr key={pl.line_id}>
                                        <td>
                                            <div className="text-xs font-mono">{pl.sku}</div>
                                            <div className="text-sm">{pl.name}</div>
                                        </td>
                                        <td className="text-right tabular-nums">{pl.quantity} {pl.unit}</td>
                                        <td className="text-right tabular-nums text-amber-600">{remaining}</td>
                                        <td><Input data-testid={`gr-accept-${i}`} type="number" min="0" max={remaining} step="0.01" className="h-9 text-right"
                                                   value={lines[i]?.accepted_qty || 0}
                                                   onChange={(e)=>{
                                                       upd(i, "accepted_qty", e.target.value);
                                                       upd(i, "received_qty", e.target.value);
                                                   }}/></td>
                                        <td><Input type="number" min="0" step="0.01" className="h-9 text-right"
                                                   value={lines[i]?.rejected_qty || 0}
                                                   onChange={(e)=>upd(i, "rejected_qty", e.target.value)}/></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={()=>setOpen(false)}>İptal</Button>
                    <Button data-testid="save-gr" onClick={submit}>Mal Kabul Yap</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function InvoiceDialog({ open, setOpen, po, grs, onDone }) {
    const [invNumber, setInvNumber] = useState("");
    const [invDate, setInvDate] = useState(new Date().toISOString().slice(0, 10));
    const [lines, setLines] = useState([]);

    useEffect(() => {
        if (open) {
            // pre-fill from PO with received qty
            setLines(po.lines.filter(l => l.received_qty > 0).map(l => ({
                po_line_id: l.line_id, product_id: l.product_id, sku: l.sku, name: l.name, unit: l.unit,
                quantity: l.received_qty, unit_price: l.unit_price, vat_pct: l.vat_pct,
            })));
        }
    }, [open, po]);

    const upd = (i, f, v) => { const nl = [...lines]; nl[i][f] = v; setLines(nl); };

    const submit = async () => {
        if (!invNumber) return toast.error("Tedarikçi fatura no zorunlu");
        try {
            const payload = {
                supplier_invoice_number: invNumber, invoice_date: invDate,
                po_id: po.id, gr_ids: grs.map(g=>g.id),
                currency: po.currency, exchange_rate: po.exchange_rate,
                lines: lines.map(l => ({
                    po_line_id: l.po_line_id, product_id: l.product_id, sku: l.sku, name: l.name, unit: l.unit,
                    quantity: Number(l.quantity), unit_price: Number(l.unit_price), vat_pct: Number(l.vat_pct),
                })),
            };
            const res = await api.post("/invoices", payload);
            const match = res.data.match_status;
            if (match === "matched") toast.success("Fatura eşleşti ✓");
            else toast.warning(`Fatura kaydedildi ancak ${res.data.discrepancies.length} uyumsuzluk var`);
            setOpen(false); onDone();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-3xl">
                <DialogHeader><DialogTitle>Fatura Kaydı — {po.number}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                    <F label="Tedarikçi Fatura No *"><Input data-testid="inv-number" value={invNumber} onChange={(e)=>setInvNumber(e.target.value)}/></F>
                    <F label="Fatura Tarihi"><Input type="date" value={invDate} onChange={(e)=>setInvDate(e.target.value)}/></F>
                </div>
                <div className="border border-border rounded-md overflow-hidden mt-2">
                    <table className="data-table">
                        <thead><tr><th>Ürün</th><th className="text-right">Miktar</th><th className="text-right">Birim Fiyat</th><th className="text-right">KDV %</th></tr></thead>
                        <tbody>
                            {lines.map((l, i) => (
                                <tr key={i}>
                                    <td><div className="text-xs font-mono">{l.sku}</div><div className="text-sm">{l.name}</div></td>
                                    <td><Input data-testid={`inv-qty-${i}`} type="number" step="0.01" className="h-9 text-right" value={l.quantity} onChange={(e)=>upd(i, "quantity", e.target.value)}/></td>
                                    <td><Input data-testid={`inv-price-${i}`} type="number" step="0.01" className="h-9 text-right" value={l.unit_price} onChange={(e)=>upd(i, "unit_price", e.target.value)}/></td>
                                    <td><Input type="number" className="h-9 text-right" value={l.vat_pct} onChange={(e)=>upd(i, "vat_pct", e.target.value)}/></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={()=>setOpen(false)}>İptal</Button>
                    <Button data-testid="save-inv" onClick={submit}>Kaydet & Eşleştir</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function F({label,children}){return<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>}
